import { Box, Button, FormControl, FormControlLabel, InputLabel, MenuItem, Select, Stack, Switch, TextField, Typography, Collapse, InputAdornment, Divider, ListSubheader, Grid } from "@mui/material";
import { useState, useMemo } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { colors, alphaVar } from "../../../ui/colors";
import { menuPropsModal } from "../../../../utils";
import type { Job } from "../../interfaces/job.interfaces";
import type { ScopeOfWork } from "../../../scope-of-work/types/scope-of-work.interface";
import { useModals } from "src/context/ModalContext";
import { usePatchJob } from "../../../clients/hooks/usePatchJob";
import { useQuoteServicesQuery } from "../../../quotes/quote-create-stepper/basic-quote/hooks/useQuoteServicesQuery";
import { ServiceAliasField } from "@components/common/ServiceAliasField";
import { EFrequencyType, JOB_FREQUENCY_OPTIONS } from "../../../scope-of-work/helpers";

const MEASURE_OPTIONS = [
  { group: "Area", value: "sqft", label: "Square Feet" },
  { group: "Area", value: "sqm", label: "Square Meters" },
  { group: "Time", value: "minutes", label: "Minute(s)" },
  { group: "Time", value: "hours", label: "Hour(s)" },
  { group: "Time", value: "days", label: "Day(s)" },
  { group: "Time", value: "weeks", label: "Week(s)" },
  { group: "Time", value: "months", label: "Month(s)" },
  { group: "Other", value: "per_item", label: "Per Item" },
  { group: "Other", value: "unique", label: "One Time" },
];

const groupOptions = (options: { group?: string; value: string; label: string }[]) => {
  const grouped: Record<string, { group: string; options: { value: string; label: string }[] }> = {};

  options.forEach((option) => {
    const groupName = option.group || "Other";
    if (!grouped[groupName]) {
      grouped[groupName] = { group: groupName, options: [] };
    }
    grouped[groupName].options.push({ value: option.value, label: option.label });
  });

  return Object.values(grouped);
};

const INPUT_HEIGHT = 40;

const baseInputStyles = {
  "& .MuiInputLabel-root": {
    color: colors.gray[400],
    fontSize: "14px",
    fontWeight: 700,
    backgroundColor: "var(--fx-surface-base, #FFFFFF)",
    padding: "0 4px",
    zIndex: 1,
    transform: "translate(12px, -2px) scale(0.85)",
    transition: "all 0.2s ease-out",
    pointerEvents: "none",

    "&.Mui-focused, &.MuiFormLabel-filled, &.Mui-error": {
      color: colors.orange.base,
      transform: "translate(12px, -2px) scale(0.85)",
    },
  },

  "& .MuiInputLabel-root.Mui-error": {
    color: colors.gray[400],
  },

  "&.Mui-error fieldset": {
    borderColor: `${colors.danger[500]} !important`,
  },

  "& .MuiOutlinedInput-root": {
    height: INPUT_HEIGHT,
    borderRadius: "12px",
    backgroundColor: "var(--fx-surface-sunken, #F8FAFC)",
    marginTop: "10px",
    transition: "all 0.2s ease-in-out",

    "& fieldset": {
      border: `1px solid ${colors.gray[200]}`,
      transition: "border-color 0.2s ease-in-out",
      "& legend": {
        display: "none",
      },
    },

    "&:hover fieldset": {
      borderColor: colors.gray[300],
    },

    "&.Mui-focused": {
      backgroundColor: "var(--fx-surface-base, #FFFFFF)",
      "& fieldset": {
        border: `2px solid ${colors.orange.base}`,
      },
    },
  },
};

const multilineStyles = {
  ...baseInputStyles,
  "& .MuiOutlinedInput-root": {
    ...baseInputStyles["& .MuiOutlinedInput-root"],
    height: "auto",
    minHeight: 120,
    alignItems: "flex-start",
    padding: "12px 16px",
  },
};

const formatMoney = (value: number) => new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(value);

type Props = {
  job: any;
  scopeOfWork?: any;
};

export function JobBasicEdit({ job, scopeOfWork }: Props) {
  const queryClient = useQueryClient();
  const { closeByKey } = useModals();
  const { mutate: patchJob, isPending } = usePatchJob();
  const { data: servicesData, isLoading: isServicesLoading } = useQuoteServicesQuery();
  const services = servicesData?.items || [];

  // Parse initial values
  const serviceCost = useMemo(() => {
    const scope = scopeOfWork;
    const service = scope?.typeOfService;

    // Resolve initial price and units
    const costOverride = scope?.costOverride ? (typeof scope.costOverride === "string" ? JSON.parse(scope.costOverride) : scope.costOverride) : null;
    const initialPrice = costOverride?.value ?? costOverride?.price ?? costOverride?.amount ?? service?.baseRatePerHour ?? "";
    const initialUnits = costOverride?.units ?? scope?.averagePeople ?? 1;
    const initialMeasure = costOverride?.by ?? costOverride?.measure ?? "unit";

    return {
      price: String(initialPrice),
      units: String(initialUnits),
      measure: String(initialMeasure),
    };
  }, [scopeOfWork]);

  const [service, setService] = useState(scopeOfWork?.typeOfService?.name || scopeOfWork?.name || "");
  const [serviceUid, setServiceUid] = useState(scopeOfWork?.typeOfServiceUid || scopeOfWork?.typeOfService?.uid || "");
  const [measure, setMeasure] = useState(serviceCost.measure);
  const [frequencyType, setFrequencyType] = useState<string>(scopeOfWork?.frequencyType || EFrequencyType.WEEKLY);
  const [useServiceNameOverride, setUseServiceNameOverride] = useState(Boolean(scopeOfWork?.serviceNameOverride));
  const [serviceNameOverride, setServiceNameOverride] = useState(scopeOfWork?.serviceNameOverride || "");
  const [notes, setNotes] = useState(scopeOfWork?.meta?.notes || "");
  const [price, setPrice] = useState(serviceCost.price);
  const [units, setUnits] = useState(serviceCost.units);
  const [wasSubmitted, setWasSubmitted] = useState(false);

  const priceNum = Number(price) || 0;
  const unitsNum = Number(units) || 0;
  const total = priceNum * unitsNum;

  const isFormValid =
    Boolean(service) &&
    Boolean(measure) &&
    Boolean(frequencyType) &&
    priceNum > 0 &&
    unitsNum > 0 &&
    (!useServiceNameOverride || Boolean(serviceNameOverride?.trim()));

  const handleSave = () => {
    setWasSubmitted(true);
    if (!isFormValid) return;

    const payload = {
      type: "basic",
      scopeOfWork: {
        name: service,
        typeOfServiceUid: serviceUid,
        averagePeople: unitsNum,
        squareFootage: "0-1000",
        typeOfService: {
          costBias: 1,
          baseRatePerHour: priceNum,
        },
        frequencyType: frequencyType,
        meta: {
          notes: notes || "",
        },
        costOverride: {
          by: measure,
          value: priceNum,
          units: unitsNum,
          currency: "USD",
        },
        clientUid: job.clientUid,
        coverageAreaId: job.coverageAreaId,
        companyId: job.companyId,
        serviceNameOverride: useServiceNameOverride
  ? serviceNameOverride.trim()
  : null,
      },
    };

    patchJob(
      { jobId: job.id, payload },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({ queryKey: ["scopeOfWork"] });
          queryClient.invalidateQueries({ queryKey: ["job", String(job.id)] });
          closeByKey(`Job-Edit-${job.id}`);
        },
      },
    );
  };

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, maxWidth: 650, mx: "auto" }}>
      {/* Header */}
      <Stack spacing={0.5} sx={{ mb: 3 }}>
        <Typography sx={{ fontWeight: 700, fontSize: 18, color: colors.gray[800] }}>Edit Job Service Details</Typography>
        <Typography sx={{ fontSize: 13, color: colors.gray[500] }}>
          Modify the service type, unit of measure, pricing, name overrides, and internal instructions.
        </Typography>
      </Stack>

      <Grid container spacing={2.5} sx={{ mb: 2.5 }}>
        <Grid item xs={12} sm={4}>
          <FormControl fullWidth sx={baseInputStyles} error={wasSubmitted && !service}>
            <InputLabel>Service Type</InputLabel>
            <Select
              label="Service Type"
              value={service}
              MenuProps={menuPropsModal}
              disabled={isServicesLoading}
              onChange={(e) => {
                setService(e.target.value);
                const selected = services.find((s: any) => s.name === e.target.value);
                if (selected) {
                  setServiceUid(selected.uid);
                }
              }}
            >
              {services.map((srv: any) => (
                <MenuItem key={srv.name} value={srv.name}>
                  {srv.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12} sm={4}>
          <FormControl fullWidth sx={baseInputStyles} error={wasSubmitted && !measure}>
            <InputLabel>Unit of Measure</InputLabel>
            <Select label="Unit of Measure" value={measure} MenuProps={menuPropsModal} onChange={(e) => setMeasure(e.target.value)}>
              {groupOptions(MEASURE_OPTIONS).flatMap((option) => [
                option.group ? (
                  <ListSubheader key={option.group} sx={{ fontWeight: 700, color: colors.gray[400] }}>
                    {option.group}
                  </ListSubheader>
                ) : null,
                ...option.options.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                )),
              ])}
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12} sm={4}>
          <FormControl fullWidth sx={baseInputStyles} error={wasSubmitted && !frequencyType}>
            <InputLabel>Frequency</InputLabel>
            <Select
              label="Frequency"
              value={frequencyType}
              MenuProps={menuPropsModal}
              onChange={(e) => setFrequencyType(e.target.value)}
            >
              {JOB_FREQUENCY_OPTIONS.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
      </Grid>

      {/* Custom Service Name switch and toggle */}
    <Box sx={{ mb: 2.5 }}>
  <ServiceAliasField
    enabled={useServiceNameOverride}
    value={serviceNameOverride}
    originalServiceName={service}
    onEnabledChange={setUseServiceNameOverride}
    onValueChange={setServiceNameOverride}
    error={
      wasSubmitted &&
      useServiceNameOverride &&
      !serviceNameOverride.trim()
    }
    helperText={
      wasSubmitted &&
      useServiceNameOverride &&
      !serviceNameOverride.trim()
        ? "Enter a custom service alias."
        : undefined
    }
  />
</Box>

      {/* Row 3: Notes & Pricing */}
      <Grid container spacing={2.5} sx={{ mb: 2.5 }} alignItems="stretch">
        <Grid item xs={12} sm={6} sx={{ display: "flex" }}>
          <TextField
            label="Internal Notes"
            placeholder="Add specific instructions for this service..."
            multiline
            fullWidth
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            sx={{
              ...multilineStyles,
              height: "100%",
              "& .MuiOutlinedInput-root": {
                ...multilineStyles["& .MuiOutlinedInput-root"],
                height: "100%",
              },
            }}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <Stack spacing={2.5} sx={{ height: "100%", justifyContent: "space-between" }}>
            <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
              <TextField
                label="Unit Price"
                type="number"
                fullWidth
                error={wasSubmitted && priceNum <= 0}
                value={price}
                onChange={(e) => setPrice(e.target.value)}
                onKeyDown={(e) => ["e", "E", "+", "-"].includes(e.key) && e.preventDefault()}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Typography sx={{ fontSize: 14, color: colors.gray[500], fontWeight: 600 }}>$</Typography>
                    </InputAdornment>
                  ),
                }}
                sx={baseInputStyles}
              />

              <TextField
                label="Quantity"
                type="number"
                fullWidth
                error={wasSubmitted && unitsNum <= 0}
                value={units}
                onChange={(e) => setUnits(e.target.value)}
                onKeyDown={(e) => ["e", "E", "+", "-"].includes(e.key) && e.preventDefault()}
                sx={baseInputStyles}
              />
            </Box>

            <Box
              sx={{
                p: 2,
                borderRadius: "12px",
                background: `linear-gradient(135deg, ${alphaVar(colors.orange.base, 0.07)} 0%, ${alphaVar(colors.orange.base, 0.03)} 100%)`,
                border: `1px solid ${alphaVar(colors.orange.base, 0.15)}`,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                height: "56px",
              }}
            >
              <Typography variant="caption" sx={{ color: colors.gray[600], fontWeight: 700, letterSpacing: 0.4 }}>
                Line Total
              </Typography>
              <Typography variant="h5" sx={{ fontWeight: 900, color: colors.gray[900], lineHeight: 1.1 }}>
                {formatMoney(total)}
              </Typography>
            </Box>
          </Stack>
        </Grid>
      </Grid>

      {wasSubmitted && !isFormValid && (
        <Typography variant="caption" color="error" sx={{ fontWeight: 600, display: "block", textAlign: "center", mb: 2 }}>
          Please correct all errors in the form before saving.
        </Typography>
      )}

      {/* Footer Actions */}
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="flex-end"
        spacing={2}
        sx={{ mt: 3, pt: 2, borderTop: `1px solid ${colors.gray[100]}` }}
      >
        <Button
          variant="outlined"
          onClick={() => closeByKey(`Job-Edit-${job.id}`)}
          disabled={isPending}
          sx={{ textTransform: "none", borderRadius: 2 }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={isPending}
          sx={{
            textTransform: "none",
            fontWeight: 700,
            borderRadius: 2,
            bgcolor: colors.orange.base,
            "&:hover": { bgcolor: colors.orange[600] },
          }}
        >
          {isPending ? "Saving..." : "Save Changes"}
        </Button>
      </Stack>
    </Box>
  );
}
